import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Loader2, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, ArrowLeft, FileText, Shield, ClipboardList, RefreshCw } from 'lucide-react';
import SafetyReportModal from '../../components/SafetyReportModal';
import { useAlert } from '../../context/AlertContext';
import { useSite } from '../../context/SiteContext';
import TabNavigation from '../../components/TabNavigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExecutiveDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useAlert();
  const { activeSiteId, isCorpUser } = useSite();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);

  useEffect(() => {
    fetchDashboard();
    // Refresh every 30 seconds
    const interval = setInterval(fetchDashboard, 30000);
    return () => clearInterval(interval);
  }, [activeSiteId]);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('token');
      const siteParam = activeSiteId ? `?site_id=${activeSiteId}` : '';
      const response = await axios.get(`${API}/dashboard/executive${siteParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load dashboard');
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    setShowReportModal(true);
    
    // Give React time to render the modal before starting the long API call
    await new Promise(resolve => setTimeout(resolve, 100));
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/export/safety-report`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 60000  // 60 second timeout for AI processing
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `Saarv_Organizational_Safety_Report_${today}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      // Wait to show completion
      await new Promise(resolve => setTimeout(resolve, 1000));
      setShowReportModal(false);


      // Show success message
      setTimeout(() => {
        showSuccess('Organizational Safety Report downloaded successfully! The report contains: Executive summary, Key performance indicators, Red flags and green flags, SSEM maturity assessment, Strategic recommendations, Future roadmap');
      }, 500);
    } catch (error) {
      console.error('Failed to generate report:', error);
      setShowReportModal(false);
      if (error.response?.status === 401) {
        showError('Session expired. Please login again.');
        window.location.href = '/login';
      } else {
        showError('Failed to generate safety report. Please try again.');
      }
    } finally{
      setGeneratingReport(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const { pss, leading_indicators, ssem_overall_level, ssem_overall_level_name, ltifr_current, ai_summary, anomalies, top_risks } = dashboard;

  const getPSSColor = (score) => {
    if (score === null || score === undefined) return 'text-gray-600 bg-gray-50';
    if (score >= 80) return 'text-green-600 bg-green-50';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const isAiSummaryError = (summary) => {
    if (!summary) return true;
    return summary.startsWith('AI summary unavailable') || summary.startsWith('AI summary generation failed');
  };

  const getSeverityColor = (severity) => {
    const colors = {
      Low: 'bg-green-100 text-green-800',
      Medium: 'bg-yellow-100 text-yellow-800',
      High: 'bg-orange-100 text-orange-800',
      Critical: 'bg-red-100 text-red-800'
    };
    return colors[severity] || 'bg-gray-100 text-gray-800';
  };

  // Format a 0-100 numeric percentage. Whole numbers render without trailing
  // zeros (80 → "80%", 100 → "100%"), fractional values get two decimals
  // (66.6666 → "66.67%"). Returns "—" for null / undefined so a missing
  // metric doesn't render as "null%".
  const formatPercent = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
    const n = Number(value);
    return n % 1 === 0 ? `${n}%` : `${n.toFixed(2)}%`;
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="executive-dashboard">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="p-6">
      {/* Header with Back Button */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <Button 
              variant="outline" 
              onClick={() => navigate('/')} 
              className="mb-4"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">Executive Safety Dashboard</h1>
            <p className="text-gray-600 mt-1">
              Real-time safety performance and leading indicators
              {dashboard?.scope && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200">
                  Scope: {dashboard.scope}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-4">
            {isCorpUser && (
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard/corporate-safety')}
              >
                Corporate Dashboard
              </Button>
            )}
            <Button
              onClick={handleGenerateReport}
              disabled={generatingReport}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all"
            >
              <FileText className="w-5 h-5 mr-2" />
              {generatingReport ? 'Generating...' : 'Generate Safety Report'}
            </Button>
          </div>
        </div>
      </div>

      {/* Top Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {/* Predictive Safety Score */}
        <Card className={`${getPSSColor(pss.score)} border-2`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Predictive Safety Score</CardTitle>
          </CardHeader>
          <CardContent>
            {pss.score !== null && pss.score !== undefined ? (
              <>
                <div className="text-3xl font-bold">{pss.score}</div>
                <div className="text-sm mt-1">{pss.risk_level}</div>
                {pss.trend && (
                  <div className="flex items-center mt-2 text-xs">
                    {pss.trend === 'Improving' ? (
                      <TrendingUp className="w-4 h-4 mr-1" />
                    ) : (
                      <TrendingDown className="w-4 h-4 mr-1" />
                    )}
                    {pss.trend}
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="text-lg font-semibold text-gray-500">{pss.risk_level || 'No Data'}</div>
                <div className="text-xs text-gray-400 mt-2">{pss.message || 'Start recording incidents, CAPAs, or audits to calculate your safety score'}</div>
              </>
            )}
          </CardContent>
        </Card>

        {/* LTIFR */}
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/incidents')}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">LTIFR (12-Month)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">{leading_indicators.ltifr_12m_rolling}</div>
            <div className="text-sm text-gray-600 mt-1">Lost Time Injury Frequency Rate</div>
            <div className="text-xs text-gray-500 mt-2">Current Month: {ltifr_current}</div>
          </CardContent>
        </Card>

        {/* High-Severity Open Incidents (post hazards/incidents split) */}
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/incidents')}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">High-Severity Open Incidents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">{leading_indicators.high_severity_incidents_open ?? 0}</div>
            <div className="text-sm text-gray-600 mt-1">
              Requiring immediate attention
              {typeof leading_indicators.high_risk_hazards_open === 'number' && leading_indicators.high_risk_hazards_open > 0 && (
                <span className="block text-xs text-gray-500 mt-0.5">
                  + {leading_indicators.high_risk_hazards_open} open HIRA hazards
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* SSEM Level */}
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/ssem')}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">SSEM Maturity Level</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">
              {ssem_overall_level ? `Level ${ssem_overall_level}` : 'Not Assessed'}
            </div>
            <div className="text-sm text-gray-600 mt-1">
              {ssem_overall_level_name || 'Safety Excellence Model'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Summary */}
      <Card className={`mb-6 ${isAiSummaryError(ai_summary) ? 'border-yellow-200 bg-yellow-50' : 'border-blue-200 bg-blue-50'}`}>
        <CardHeader>
          <CardTitle className={`flex items-center ${isAiSummaryError(ai_summary) ? 'text-yellow-800' : 'text-blue-900'}`}>
            {isAiSummaryError(ai_summary) ? (
              <AlertTriangle className="w-5 h-5 mr-2" />
            ) : (
              <CheckCircle className="w-5 h-5 mr-2" />
            )}
            AI Safety Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className={`leading-relaxed ${isAiSummaryError(ai_summary) ? 'text-yellow-700' : 'text-blue-900'}`}>{ai_summary}</p>
        </CardContent>
      </Card>

      {/* Leading Indicators Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/observations')}>
          <CardHeader>
            <CardTitle className="text-lg">Observations & Reporting</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Near Miss Rate (per 100)</span>
              <span className="font-semibold">{leading_indicators.near_miss_rate}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Unsafe Acts Reported</span>
              <span className="font-semibold">{leading_indicators.unsafe_acts_reported}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Unsafe Conditions</span>
              <span className="font-semibold">{leading_indicators.unsafe_conditions_reported}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/capa')}>
          <CardHeader>
            <CardTitle className="text-lg">CAPA Performance</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Closure Rate</span>
              <span className="font-semibold">{formatPercent(leading_indicators.capa_closure_rate)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">High-Risk Overdue</span>
              <span className="font-semibold text-red-600">{leading_indicators.high_risk_capas_overdue}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Effectiveness Score</span>
              <span className="font-semibold">{formatPercent(leading_indicators.capa_effectiveness_score)}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/audits')}>
          <CardHeader>
            <CardTitle className="text-lg">Compliance & Audit</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Compliance Score</span>
              <span className="font-semibold">{formatPercent(leading_indicators.compliance_score_monthly)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Audit Compliance</span>
              <span className="font-semibold">{formatPercent(leading_indicators.audit_compliance_score)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Competence Score</span>
              <span className="font-semibold">{formatPercent(leading_indicators.org_competence_score)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* PTW Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/ptw')}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Shield className="w-4 h-4 text-blue-600" />
              PTW Issued
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">{leading_indicators.ptw_issued || 0}</div>
            <div className="text-sm text-gray-600 mt-1">Total permits issued</div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/ptw')}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-green-600" />
              Open PTW
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{leading_indicators.active_permits || 0}</div>
            <div className="text-sm text-gray-600 mt-1">Currently active permits</div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/ptw')}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-orange-600" />
              Extended PTW
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">{leading_indicators.ptw_extended || 0}</div>
            <div className="text-sm text-gray-600 mt-1">Permits with extension requested</div>
          </CardContent>
        </Card>
      </div>

      {/* Anomalies & Top Risks */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Anomalies */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center text-orange-700">
              <AlertTriangle className="w-5 h-5 mr-2" />
              AI-Detected Anomalies
            </CardTitle>
          </CardHeader>
          <CardContent>
            {anomalies.length === 0 ? (
              <p className="text-gray-500 text-sm">No anomalies detected</p>
            ) : (
              <div className="space-y-3">
                {anomalies.map((anomaly, idx) => (
                  <div key={idx} className="border-l-4 border-orange-500 pl-3 py-2">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{anomaly.description}</span>
                      <span className={`text-xs px-2 py-1 rounded ${getSeverityColor(anomaly.severity)}`}>
                        {anomaly.severity}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">{anomaly.recommendation}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Risks — whole card opens the incidents list; each row drills
            into that specific incident's detail page. */}
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/incidents')}>
          <CardHeader>
            <CardTitle className="text-red-700">Top Open Risks</CardTitle>
          </CardHeader>
          <CardContent>
            {top_risks.length === 0 ? (
              <p className="text-gray-500 text-sm">No high-risk hazards open</p>
            ) : (
              <div className="space-y-2">
                {top_risks.slice(0, 5).map((risk, idx) => (
                  <div
                    key={risk.id || idx}
                    onClick={(e) => {
                      // Don't bubble up to the card-level "go to list" handler.
                      e.stopPropagation();
                      if (risk.id) navigate(`/incidents/${risk.id}`);
                    }}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex-1">
                      <p className="text-sm font-medium">{risk.title}</p>
                      <p className="text-xs text-gray-600">{risk.location || 'Location not specified'}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${getSeverityColor(risk.severity)}`}>
                      {risk.severity}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Safety Report Generation Modal */}
      <SafetyReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
      />
      </div>
    </div>
  );
};

export default ExecutiveDashboard;
